/**
 * Lien d'achat `/buy/:slug` d'un produit (pure, client + serveur) : la fiche produit de
 * l'admin l'affiche, `resolveBuySignupRedirect` (`oneshot-checkout.ts`) le sert.
 */

/** Slug d'URL → code licence : "starter-one-shot" → "STARTER_ONE_SHOT". */
export function slugToLicenceCode(slug: string): string {
  return slug.trim().toUpperCase().replace(/-/g, "_");
}

/** Code licence → slug d'URL : "PREMIUM_MONTHLY" → "premium-monthly". */
export function licenceCodeToSlug(code: string): string {
  return code.trim().toLowerCase().replace(/_/g, "-");
}

export type BuyLinkProblem = "noCode" | "trial" | "addon" | "inactive" | "noZohoPlan";

/**
 * Pourquoi un produit n'a pas de lien d'achat (pure, client + serveur) ; `null` = lien valide.
 * Un champ absent (formulaire de création) compte comme vide ; `isActive` absent = actif.
 */
export function buyLinkProblem(product: {
  code?: string | null;
  isActive?: boolean | null;
  isTrial?: boolean | null;
  tier?: string | null;
  zohoPlanCode?: string | null;
}): BuyLinkProblem | null {
  if (!product.code?.trim()) return "noCode";
  if (product.isTrial) return "trial";
  if (product.tier === "addon") return "addon";
  if (product.isActive === false) return "inactive";
  if (!product.zohoPlanCode?.trim()) return "noZohoPlan";
  return null;
}

/** Plafond de quantité d'un achat quand la fiche produit n'en fixe pas (lot S4, décision du 22/10/2026). */
export const BUY_MAX_QUANTITY_DEFAULT = 200;

export interface PurchasableProduct {
  code?: string | null;
  isActive?: boolean | null;
  isTrial?: boolean | null;
  tier?: string | null;
  zohoPlanCode?: string | null;
  maxQuantity?: number | null;
  isOneShot?: boolean | null;
}

/**
 * Produit achetable par ce code de plan Zoho (pure, client + serveur) — lot S4 du chantier pricing.
 * Même règle que les liens `/buy` (`buyLinkProblem`) : actif, pas d'essai, pas d'option (`addon`),
 * avec un plan Zoho ÉGAL au code demandé. `null` = code refusé à l'achat. Plusieurs fiches peuvent
 * partager un code Zoho (colonne non unique) : la première achetable l'emporte.
 */
export function purchasableProductFor<T extends PurchasableProduct>(products: readonly T[], planCode: string): T | null {
  const code = planCode?.trim();
  if (!code) return null;
  return products.find((p) => p.zohoPlanCode?.trim() === code && buyLinkProblem(p) === null) ?? null;
}

/** Plafond de quantité d'un produit : celui de sa fiche, sinon le plafond général. */
export function purchaseMaxQuantity(product: PurchasableProduct, globalMax = BUY_MAX_QUANTITY_DEFAULT): number {
  return typeof product.maxQuantity === "number" && product.maxQuantity >= 1 ? product.maxQuantity : globalMax;
}
